import {
  DEFAULT_CHECKOUT_TRUCK_CAPACITY,
  type ResolvedTruckCapacity,
} from "./truck-capacity";

export const MAX_TRUCKLOADS_PER_QUOTE = 10;

export interface CheckoutMinimumFields {
  minOrderQuantity?: number | null;
  minCheckoutQuantity?: number | null;
}

export function getCheckoutMinimumQuantity(
  product: CheckoutMinimumFields,
): number {
  return Math.max(
    1,
    product.minOrderQuantity ?? 1,
    product.minCheckoutQuantity ?? 1,
  );
}

export function isCheckoutQuantityAllowed(
  quantity: number,
  minimum: number,
): boolean {
  return Number.isSafeInteger(quantity) && quantity >= minimum;
}

export function canScheduleTruckloads(
  minimum: number,
  capacity: number | ResolvedTruckCapacity[] | undefined,
  quantity: number,
): boolean {
  const resolvedCapacity =
    typeof capacity === "number"
      ? capacity
      : capacity
        ? Math.max(0, ...capacity.map((option) => option.capacity))
        : DEFAULT_CHECKOUT_TRUCK_CAPACITY;
  if (
    !Number.isSafeInteger(quantity) ||
    !Number.isSafeInteger(minimum) ||
    !Number.isSafeInteger(resolvedCapacity) ||
    quantity < minimum ||
    minimum < 1 ||
    resolvedCapacity < 1
  )
    return false;
  const count = Math.ceil(quantity / resolvedCapacity);
  return count <= MAX_TRUCKLOADS_PER_QUOTE;
}

export function getRecommendedTruckloadQuantity(
  minimum: number,
  capacity: number,
): number {
  return Math.ceil(minimum / capacity) * capacity;
}

export interface PlannedTruckload {
  quantity: number;
  truckType: string;
  truckCapacity: number;
}

export function splitCheckoutTruckloads(
  quantity: number,
  options: ResolvedTruckCapacity[],
): PlannedTruckload[] {
  const truck =
    options.find(
      (option) =>
        Math.ceil(quantity / option.capacity) <= MAX_TRUCKLOADS_PER_QUOTE,
    ) ?? options[0];
  if (!truck || !Number.isSafeInteger(quantity) || quantity < 1) return [];
  return Array.from(
    { length: Math.ceil(quantity / truck.capacity) },
    (_, index) => ({
      quantity: Math.min(truck.capacity, quantity - index * truck.capacity),
      truckType: truck.truckType,
      truckCapacity: truck.capacity,
    }),
  );
}

export function planCheckoutTruckloads(
  quantity: number,
  options: ResolvedTruckCapacity[],
): PlannedTruckload[] {
  return splitCheckoutTruckloads(quantity, options).slice(
    0,
    MAX_TRUCKLOADS_PER_QUOTE + 1,
  );
}

const ROUND_QUANTITY_STEPS = [
  1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000,
];

function largestRoundStepAtMost(value: number): number | null {
  if (!Number.isFinite(value) || value < 1) return null;
  let largest = 1;
  for (const candidate of ROUND_QUANTITY_STEPS) {
    if (candidate <= value) largest = candidate;
  }
  return largest;
}

export function getListingQuantityStep(
  minimum: number,
  truckCapacity: number,
): number {
  const roundTenthOfTruck = largestRoundStepAtMost(truckCapacity / 10);
  if (Number.isSafeInteger(minimum) && minimum > 1) {
    return Math.min(minimum, roundTenthOfTruck ?? minimum);
  }
  return roundTenthOfTruck ?? 1;
}

export function stepQuantityUp(quantity: number, step: number): number {
  return (Math.floor(quantity / step) + 1) * step;
}

export function stepQuantityDown(
  quantity: number,
  step: number,
  minimum: number,
): number {
  return Math.max(minimum, (Math.ceil(quantity / step) - 1) * step);
}
